import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAddPermissionsUrlToUse,
  getPermissionGroupsUrlToUse,
  getPermissionsManagerUrlToUse,
  getRemovePermissionsUrlToUse
} from "./helper";

const queryKeyBase = "ddc-pages-user-permissions-manager";

export function usePermissionsForUserGet(userId?: string | undefined) {
  return useQuery({
    queryKey: [queryKeyBase, userId],
    enabled: !!userId,
    refetchOnWindowFocus: false,
    queryFn: () => fetchUserPermissionsGet(userId || "")
  });
}

export function usePermissionGroupsGet() {
  return useQuery({
    queryKey: [queryKeyBase],
    enabled: true,
    refetchOnWindowFocus: false,
    queryFn: () => fetchPermissionGroupsGet()
  });
}

export interface AddPermissionsInterface {
  userId: string;
  permission: string;
}

export interface RemovePermissionsInterface {
  userId: string;
  permissionGroup: string;
}

export function usePermissionGroupsForUserAdd(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase, userId] });
    },
    mutationFn: ({ userId, permission }: AddPermissionsInterface) => {
      return fetchUserPermissionsAdd(userId, permission);
    }
  });
}

export function usePermissionGroupsForUserRemove(
  userId: string,
  permissionGroup: string
) {
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, userId]
      });
    },
    mutationFn: ({ userId, permissionGroup }: RemovePermissionsInterface) => {
      return fetchUserPermissionsRemove(userId, permissionGroup);
    }
  });
}

const fetchUserPermissionsAdd = async (userId: string, permission: string) => {
  const url = getAddPermissionsUrlToUse(userId, permission);
  const fetchResult = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    }
  });
  if (!fetchResult.ok) {
    throw new Error(
      `Failed to add ${userId} to group ${permission}: ${fetchResult.statusText}`
    );
  }
  const result = await fetchResult.json();
  return result;
};

const fetchUserPermissionsRemove = async (
  userId: string,
  permissionGroup: string
) => {
  const url = getRemovePermissionsUrlToUse(userId, permissionGroup);
  const fetchResult = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    }
  });
  if (!fetchResult.ok) {
    throw new Error(
      `Failed to remove ${permissionGroup} from user ${userId}: ${fetchResult.statusText}`
    );
  }
  const result = await fetchResult.json();
  return result;
};

const fetchUserPermissionsGet = async (userId: string) => {
  const url = getPermissionsManagerUrlToUse(userId);
  const fetchResult = await fetch(url);
  if (!fetchResult.ok) {
    throw new Error(
      `Failed to fetch permissions for ${userId}: ${fetchResult.statusText}`
    );
  }
  const result = await fetchResult.json();
  return result;
};

const fetchPermissionGroupsGet = async () => {
  const url = getPermissionGroupsUrlToUse();
  const fetchResult = await fetch(url);
  if (!fetchResult.ok) {
    throw new Error(
      `Failed to fetch permission groups: ${fetchResult.statusText}`
    );
  }
  const result = await fetchResult.json();
  return result;
};
